import React, { useMemo, useState } from 'react';
import { Folder, LayoutGrid, List, MoreHorizontal, Network, Plus } from 'lucide-react';
import { TRACKS, GROUP_NAMES, TrackStatus } from '../../data/workspaceTracks';
import { EmptyState, Glyph, Panel, PrimaryAction, Row, RowText, ScreenHeader, SearchField, Segmented, SelectPill, StatusRing, SURFACE } from './ui';

// ── Tracks ───────────────────────────────────────────────────────────────────
// A flat, filterable library of tracks. Each row shows the track's name and the
// group that owns it, plus its type. Filters: group, status, type, sort, layout.
//
// Rebuilt on the shared app primitives in ./ui.tsx — see that file for why the
// grid of individually bordered grey boxes became one warm panel of
// hairline-divided rows. No copy or data changed.
// ─────────────────────────────────────────────────────────────────────────────

const STATUSES: {
  value: TrackStatus;
  label: string;
}[] = [{
  value: 'active',
  label: 'Active'
}, {
  value: 'dormant',
  label: 'Dormant'
}, {
  value: 'archived',
  label: 'Archived'
}];
const TYPES = ['All track types', 'area', 'project', 'exploration', 'product / skills backlog'];
const LAYOUTS = [['list', List], ['grid', LayoutGrid], ['tree', Network]] as const;
export function TracksView() {
  const [group, setGroup] = useState('Group');
  const [status, setStatus] = useState<TrackStatus>('active');
  const [type, setType] = useState(TYPES[0]);
  const [sort, setSort] = useState<'Name' | 'Group'>('Name');
  const [layout, setLayout] = useState<'list' | 'grid' | 'tree'>('grid');
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const rows = TRACKS.filter(t => t.status === status).filter(t => group === 'Group' || t.group === group).filter(t => type === TYPES[0] || t.type === type).filter(t => t.name.toLowerCase().includes(query.trim().toLowerCase()));
    return [...rows].sort((a, b) => sort === 'Name' ? a.name.localeCompare(b.name) : a.group.localeCompare(b.group) || a.name.localeCompare(b.name));
  }, [group, status, type, sort, query]);

  // The row's contents are identical in both layouts; only the container
  // differs — a joined list, or separate cards in a grid.
  const rows = visible.map(track => <>
      <Glyph icon={Folder} data-id="element-2968" data-mp="1d04wj1y3h7u3" />
      <RowText title={track.name} meta={`${track.group} · ${track.type}`} data-id="element-2969" data-mp="180s1htx2kugf" />
      <StatusRing status={track.status} data-id="element-2970" data-mp="182v0h3g79xxa" />
      <button type="button" aria-label={`Options for ${track.name}`} className="flex-none rounded-md p-1 text-app-subtle opacity-0 transition-[color,opacity] duration-200 ease-out hover:text-app focus-visible:opacity-100 group-hover:opacity-100" data-id="element-2971" data-mp="24rkfmm0g0cfk">
        <MoreHorizontal size={16} data-id="element-2972" data-mp="1vwwen81whgwk" />
      </button>
    </>);
  // No padding of its own: the dashboard's content surface already provides it,
  // and both together were giving the screen a 64px gutter.
  return <div data-id="element-2973" data-mp="2oh4ofwgj5knl">
      <ScreenHeader title="Tracks" action={<PrimaryAction label="New track" data-id="element-2975" data-mp="2jns43yivw00n">
            <Plus size={15} data-id="element-2976" data-mp="2jn0hhd79lcmi" />
            New track
          </PrimaryAction>} data-id="element-2974" data-mp="2oguxo6nnv9gu" />

      {/* Controls on one line under the title, so the heading is not competing
       with five pills for the top of the screen. */}
      <div className="mt-6 flex flex-wrap items-center gap-2.5" data-id="element-2977" data-mp="2w0nges7r4o8b">
        <SelectPill value={group} onChange={setGroup} options={['Group', ...GROUP_NAMES]} label="Filter by group" data-id="element-2978" data-mp="30wumqm4281qs" />
        <SelectPill value={STATUSES.find(s => s.value === status)!.label} onChange={value => setStatus(STATUSES.find(s => s.label === value)!.value)} options={STATUSES.map(s => s.label)} label="Filter by status" data-id="element-2979" data-mp="350k70zdpbx60" />
        <SelectPill value={type} onChange={setType} options={TYPES} label="Filter by track type" data-id="element-2980" data-mp="3evc3u0zgmfzq" />
        <SelectPill value={sort} onChange={value => setSort(value as 'Name' | 'Group')} options={['Name', 'Group']} label="Sort by" prefix="Sort" data-id="element-2981" data-mp="3jq87xzhz0tt7" />
        <span className="grow" data-id="element-2982" data-mp="3splb6afi4m5h" />
        <Segmented value={layout} onChange={setLayout} options={LAYOUTS} data-id="element-2983" data-mp="3sqmx59y22t1z" />
      </div>

      <div className="mt-4" data-id="element-2984" data-mp="1jabahm89rfq">
        <SearchField value={query} onChange={setQuery} placeholder={`Search ${TRACKS.length} tracks`} label="Search tracks" data-id="element-2985" data-mp="1gfqgozay4ls" />
      </div>

      {visible.length === 0 ? <div className="mt-5" data-id="element-2986" data-mp="3fyt1h554p1ij">
          <EmptyState data-id="element-2987" data-mp="3fxhxp5bontrf">No tracks match these filters.</EmptyState>
        </div> : layout === 'grid' ? <ul className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-id="element-2988" data-mp="3tq7oro62jove">
          {visible.map((track, index) => <li key={track.name} className={`group flex items-center gap-3 px-4 py-3 transition-[border-color,box-shadow] duration-200 ease-out hover:border-app-strong hover:shadow-[0_1px_2px_rgba(20,20,24,0.05),0_8px_20px_-14px_rgba(20,20,24,0.18)] ${SURFACE}`} data-id="element-2989" data-mp="3ou8sleei12kf" data-mpi={index}>
              {rows[index]}
            </li>)}
        </ul> : <Panel className="mt-5" data-id="element-2990" data-mp="361odczrq2vz3">
          {visible.map((track, index) => <Row key={track.name} data-id="element-2991" data-mp="3603j7b9blf1r" data-mpi={index}>{rows[index]}</Row>)}
        </Panel>}
    </div>;
}